import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Calendar, ArrowRight, Zap, ShieldCheck, Clock } from "lucide-react";
import api, { resolveMedia } from "@/lib/api";
import { stripHtml } from "@/lib/seo";

/**
 * „Warum lohnt sich das?" — Value-Modul für die Newsletter-Anmeldung.
 * Zeigt konkrete Vorteile + eine Live-Vorschau des zuletzt veröffentlichten
 * Bauchronik-Eintrags als Beispiel dafür, was Abonnenten erwartet.
 */
export default function NewsletterValueTeaser() {
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    api.get("/bauchroniken")
      .then((r) => {
        const list = r.data || [];
        setLatest(list[0] || null);
      })
      .catch(() => setLatest(null));
  }, []);

  const cover =
    latest?.header_image_url
      ? resolveMedia(latest.header_image_url)
      : latest?.media?.find((m) => m.type === "image" && m.url)?.url
      ? resolveMedia(latest.media.find((m) => m.type === "image" && m.url).url)
      : latest?.media?.find((m) => m.type === "video" && m.thumbnail_url)?.thumbnail_url
      ? resolveMedia(latest.media.find((m) => m.type === "video" && m.thumbnail_url).thumbnail_url)
      : "";

  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-testid="newsletter-value-teaser">
      {/* Left: heading + benefits */}
      <div className="lg:col-span-6">
        <p className="text-xs uppercase tracking-[0.20em] text-[hsl(var(--brand-green-strong))] mb-3">
          Warum lohnt sich das?
        </p>
        <h2 className="font-semibold text-3xl sm:text-4xl leading-[1.1] text-neutral-900 mb-5">
          Alle wichtigen Bauschritte —<br className="hidden sm:block" /> direkt in Ihrem Postfach.
        </h2>
        <p className="text-neutral-600 text-base leading-relaxed max-w-lg mb-8">
          Sie müssen nichts mehr aktiv abfragen. Sobald sich am Quartier am Hornschen Tor etwas bewegt — Baugrube, Rohbau, Sperrungen, Meilensteine — bekommen Sie eine kurze, gut lesbare E-Mail mit Foto und dem Link zur ausführlichen Bauchronik.
        </p>

        <ul className="space-y-4">
          <li className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-brand-line bg-white text-[hsl(var(--brand-green-strong))]">
              <Camera className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-neutral-900">Foto- und Video-Updates</p>
              <p className="text-sm text-neutral-500 leading-snug">Neue Bauchroniken mit Bildern und Videos direkt aus Detmold.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-brand-line bg-white text-[hsl(var(--brand-green-strong))]">
              <Zap className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-neutral-900">Nur wenn es wirklich Neues gibt</p>
              <p className="text-sm text-neutral-500 leading-snug">Kein Wochenrhythmus, keine Werbung — nur bei echten Meilensteinen.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-brand-line bg-white text-[hsl(var(--brand-green-strong))]">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-neutral-900">Datenschutzkonform (Double-Opt-In)</p>
              <p className="text-sm text-neutral-500 leading-snug">Sie bestätigen die Anmeldung per Klick — jederzeit mit einem Klick wieder abbestellbar.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-brand-line bg-white text-[hsl(var(--brand-green-strong))]">
              <Clock className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-neutral-900">30 Sekunden Anmeldung</p>
              <p className="text-sm text-neutral-500 leading-snug">Nur eine E-Mail-Adresse — keine weiteren Angaben nötig.</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Right: preview card */}
      <div className="lg:col-span-6" data-testid="newsletter-value-preview">
        {latest ? (
          <div className="relative">
            {/* subtle offset "card behind card" for depth */}
            <div aria-hidden="true" className="hidden sm:block absolute -bottom-3 -right-3 top-6 left-6 border border-brand-line bg-[hsl(var(--brand-soft))]" />
            <article className="relative bg-white border border-brand-line overflow-hidden shadow-[0_20px_50px_-32px_rgba(0,0,0,0.20)]">
              {/* Faux e-mail header */}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-brand-line bg-[#fafbf7]">
                <span className="inline-block h-2 w-2 rounded-full bg-red-400" />
                <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                <span className="ml-3 text-[11px] uppercase tracking-[0.14em] text-neutral-500">
                  So sieht Ihr Newsletter aus
                </span>
              </div>
              {cover && (
                <div className="relative aspect-[16/9] overflow-hidden bg-neutral-100">
                  <img src={cover} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="px-6 py-5">
                <p className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--brand-green-strong))] mb-1.5 inline-flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" /> Bauchronik · {latest.date || "aktuelle Bauphase"}
                </p>
                <h3 className="font-semibold text-xl leading-snug text-neutral-900 mb-2">
                  Neue Bauchronik: {latest.title}
                </h3>
                <p className="text-sm text-neutral-600 line-clamp-3 mb-4">
                  {stripHtml(latest.description || "", 180) || "Der neueste Bauabschnitt am Quartier am Hornschen Tor — mit Bildern und Details."}
                </p>
                <div className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--brand-green-strong))] mb-4">
                  Zur vollständigen Bauchronik <ArrowRight className="h-4 w-4" />
                </div>
                <p className="text-[10px] text-neutral-400 pt-3 border-t border-brand-line leading-snug">
                  Beispiel-Vorschau. Sie erhalten diese Mails nur, wenn eine neue Bauchronik veröffentlicht wird.
                </p>
              </div>
            </article>
            <p className="mt-3 text-xs text-neutral-500 text-center">
              Vorschau des letzten veröffentlichten Bauchronik-Eintrags.
              <Link to={`/bauchroniken/${latest.slug}`} className="text-[hsl(var(--brand-green-strong))] hover:underline ml-1" data-testid="newsletter-value-preview-link">
                Diese Bauchronik ansehen →
              </Link>
            </p>
          </div>
        ) : (
          /* Fallback if no Bauchronik exists yet */
          <div className="border border-brand-line bg-[hsl(var(--brand-soft))] p-6 sm:p-8" data-testid="newsletter-value-preview-empty">
            <p className="text-xs uppercase tracking-[0.14em] text-[hsl(var(--brand-green-strong))] mb-2">Vorschau</p>
            <p className="text-sm text-neutral-700 leading-relaxed">
              Die ersten Bauchronik-Newsletter kommen, sobald die ersten Bauabschnitte dokumentiert sind. Sie sind dann von Anfang an dabei.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
